import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

import { I18nService } from '@core/i18n-legacy';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';
import { PreventLeaveDialogComponent } from '@recruitee/components-prevent-leave-dialog-legacy';

import { EvaluationDraftService } from './evaluation-draft.service';
import { QuickEvaluationFormService } from './quick-evaluation-form.service';
import { TemplateEvaluationFormService } from './template-evaluation-form.service';

@Injectable()
export class EvaluationPanelClearService {
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly quickForm = inject(QuickEvaluationFormService);
  private readonly templateForm = inject(TemplateEvaluationFormService);
  private readonly draftService = inject(EvaluationDraftService);

  public clearWithConfirm(isQuick: boolean, candidateId: number): void {
    const service = isQuick ? this.quickForm : this.templateForm;

    if (!service.form.dirty) {
      this.clear(isQuick, candidateId);
      return;
    }

    this.dialogService
      .openCustom(
        PreventLeaveDialogComponent,
        { width: DialogWidth.SmallX },
        {
          danger: true,
          title: this.i18n.translate('app.evaluation_panel.clear_form_confirm.title'),
          message: this.i18n.translate('app.evaluation_panel.clear_form_confirm.message'),
          discardLabel: this.i18n.translate('app.evaluation_panel.clear_form'),
          cancelLabel: this.i18n.translate('app.evaluation_panel.cancel'),
        },
      )
      .afterClosed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) this.clear(isQuick, candidateId);
      });
  }

  public clear(isQuick: boolean, candidateId: number): void {
    const service = isQuick ? this.quickForm : this.templateForm;
    service.clear();
    service.form.markAsPristine();
    this.draftService.clearDraft(candidateId);
  }

  public confirmDiscard(isQuick: boolean): Observable<boolean> {
    const service = isQuick ? this.quickForm : this.templateForm;

    if (!service.form.dirty) {
      return of(true);
    }

    return this.dialogService
      .openCustom(
        PreventLeaveDialogComponent,
        { width: DialogWidth.SmallX },
        {
          danger: true,
          title: this.i18n.translate('app.evaluation_panel.leave_evaluation_confirm.title'),
          message: this.i18n.translate('app.evaluation_panel.leave_evaluation_confirm.message'),
          discardLabel: this.i18n.translate('app.evaluation_panel.leave_evaluation_confirm.cta'),
          cancelLabel: this.i18n.translate('app.evaluation_panel.cancel'),
        },
      )
      .afterClosed.pipe(map(confirmed => !!confirmed));
  }
}
